import { useState } from "react";
import { Link } from "react-router-dom";

const UPDATED = "June 2026";

const Doc = ({ title, children, testid }) => (
  <article className="mx-auto max-w-3xl px-4 pb-32 pt-28 md:px-8" data-testid={testid}>
    <div className="label-mono">Legal · updated {UPDATED}</div>
    <h1 className="mb-10 mt-3 font-display text-4xl font-black uppercase">{title}</h1>
    <div className="space-y-6 leading-relaxed text-slate-300 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_li]:ml-5 [&_li]:list-disc">{children}</div>
  </article>
);

export function Privacy() {
  return (
    <Doc title="Privacy Policy" testid="privacy-page">
      <p>This policy explains how Fuorionda ("we") processes personal data under the EU General Data Protection Regulation (GDPR, Reg. 2016/679).</p>
      <h2>1. What we collect</h2>
      <ul>
        <li><b>Artists:</b> email, password (stored only as an encrypted hash), stage name, bio, photo, genre, city, social links, uploaded tracks, plan and payment history.</li>
        <li><b>Listeners:</b> no account. We store a random device ID in your browser, the Flames, reactions and votes you give, and an approximate city derived from your IP address to produce anonymous listener statistics.</li>
        <li><b>Organizers:</b> name, email and the details you write in a booking request, shared with the artist you contact.</li>
        <li><b>Payments:</b> handled by PayPal. We never see or store card numbers; we keep the transaction ID, amount and date.</li>
      </ul>
      <h2>2. Why and legal basis</h2>
      <ul>
        <li>To provide the service you signed up for — artist page, radio broadcasting, reports, plans (contract, art. 6.1.b).</li>
        <li>To send service emails: welcome, review results, schedules, receipts, reports (contract; optional emails can be switched off in your dashboard).</li>
        <li>To prevent fraud on Flames and charts (legitimate interest, art. 6.1.f).</li>
        <li>To meet tax and accounting obligations (legal obligation, art. 6.1.c).</li>
      </ul>
      <h2>3. Who receives data</h2>
      <p>Hosting and storage providers, our email provider (Resend), PayPal for payments, and an IP geolocation service (ipapi.co) that only receives the IP address. Public profile information is visible to everyone. We never sell personal data.</p>
      <h2>4. How long we keep it</h2>
      <p>Account data until you delete your account; payment records for 10 years as required by Italian law; listener device data for up to 24 months.</p>
      <h2>5. Your rights</h2>
      <p>You can request access, correction, deletion, restriction, portability or object to processing by <Link to="/contact" className="text-[#00F5D4]">opening a ticket</Link>. You can also complain to the Italian Data Protection Authority (Garante per la protezione dei dati personali, garanteprivacy.it).</p>
      <h2>6. Cookies</h2>
      <p>See our <Link to="/cookies" className="text-[#00F5D4]">Cookie Policy</Link>.</p>
    </Doc>
  );
}

export function Terms() {
  return (
    <Doc title="Terms of Use" testid="terms-page">
      <p>These terms govern the use of Fuorionda. By creating an account you accept them.</p>
      <h2>1. The service</h2>
      <p>Fuorionda is a curated web radio and showcase for independent artists. Admission is at our editorial discretion: paying a plan never guarantees acceptance, only more plays once accepted.</p>
      <h2>2. Your music and the broadcast license</h2>
      <ul>
        <li>You confirm you own or control <b>all rights</b> in every track you upload (composition, lyrics, master recording) and that it doesn't infringe third-party rights or include uncleared samples.</li>
        <li>You keep 100% of your rights. You grant Fuorionda a <b>non-exclusive, royalty-free, worldwide, revocable license</b> to stream your tracks on the radio and your artist page, to use your name, photo, bio and short excerpts to promote the platform (including social media posts, charts and story cards), for as long as the track is online.</li>
        <li>You can remove a track at any time from your dashboard; the license ends when the track is removed (posts already published may remain).</li>
        <li>If you are a member of a collecting society (e.g. SIAE), tell us: public performance rights are handled according to applicable law.</li>
      </ul>
      <h2>3. Plans and payments</h2>
      <ul>
        <li>Plans are subscriptions billed monthly or yearly via PayPal and renew automatically until cancelled. You can cancel at any time from your dashboard; the plan stays active until the end of the paid period. No refunds for partial periods, except where required by law.</li>
        <li>Guaranteed plays are scheduled daily; times may shift by a few minutes. If we fail to deliver the guaranteed plays of a month, we extend your plan proportionally.</li>
        <li>One-off services are delivered within 14 days. Consumers have a 14-day withdrawal right before the service is delivered.</li>
        <li>Founding Artist and prize months are free and are not converted into paid plans automatically.</li>
      </ul>
      <h2>4. Charts, Flames and battles</h2>
      <p>Charts are based on listener Flames and votes. Manipulation (fake accounts, scripts, paid Flames) leads to voided Flames and possible removal. Prizes are visibility and services, never cash.</p>
      <h2>5. Content and conduct</h2>
      <p>No hateful, illegal or infringing content. We may remove content or accounts that break these terms.</p>
      <h2>6. Liability</h2>
      <p>The service is provided "as is". We don't guarantee a number of listeners. To the extent permitted by law, our liability is limited to the amounts you paid in the last 12 months.</p>
      <h2>7. Law</h2>
      <p>Italian law applies. For consumers, the court of their residence is competent. Questions: <Link to="/contact" className="text-[#00F5D4]">contact us</Link>.</p>
    </Doc>
  );
}

export function Cookies() {
  return (
    <Doc title="Cookie Policy" testid="cookies-page">
      <p>Fuorionda uses only <b>technical cookies and local storage</b> that are strictly necessary for the site to work. We do not use advertising or profiling cookies, and no analytics trackers.</p>
      <ul>
        <li><b>access_token / refresh_token</b> — keep you logged in (up to 7 days).</li>
        <li><b>fo_device</b> (local storage) — anonymous ID used to limit Flames and votes to one per device.</li>
        <li><b>fo_cookie_ok</b> (local storage) — remembers you've seen the cookie notice.</li>
        <li><b>PayPal</b> — when you open checkout, PayPal sets its own cookies under its privacy policy.</li>
      </ul>
      <p>Technical cookies don't require consent under the Italian Garante guidelines; you can delete them from your browser settings at any time.</p>
    </Doc>
  );
}

export const CookieBanner = () => {
  const [ok, setOk] = useState(() => !!localStorage.getItem("fo_cookie_ok"));
  if (ok) return null;
  const accept = () => { localStorage.setItem("fo_cookie_ok", "1"); setOk(true); };
  return (
    <div data-testid="cookie-banner" className="glass fixed inset-x-3 top-20 z-[80] mx-auto max-w-xl rounded-2xl p-4 text-sm text-slate-300 shadow-2xl">
      We only use technical cookies needed to run the site (login, one Flame per device). No ads, no tracking. <Link to="/cookies" className="text-[#00F5D4]">Cookie Policy</Link> · <Link to="/privacy" className="text-[#00F5D4]">Privacy</Link>
      <button onClick={accept} className="btn-hot ml-3 mt-2 !px-4 !py-1.5 text-xs" data-testid="cookie-accept">OK</button>
    </div>
  );
};
